import { getExperiment, getExperimentNoExposure, manuallyLogExposure } from "./experimentClient";

export enum EXPERIMENTS {
  DOM_CONTEXT_IMPROVEMENT = "rovo_extension_dom_improvement",
  NTP_SEARCH_FILTERS = "ntp_search_filters",
  NTP_SEARCH_FILTERS_3P_CONNECTORS = "ntp_search_filters_3p_connectors",
  NTP_AGENTS_DROPDOWN = "rovo_extension_ntp_agents_dropdown",
  SITE_ADMIN_SETTINGS = "rovo-extension-site-admin-settings",
  SETTINGS_ADMIN_SITE_CONTROLS = "rovo-ext_settings_admin_site_controls",
  SESSION_REPLAY = "rovo-extension-session-replay",
  DEFAULT_TO_CUSTOM_LINKS = "rovo_extension_default_to_custom_links",
  EXTENSION_CTA_SITE_HOME = "extension-cta-site-home",
  FOUNDATION_UX_BLITZ = "rovo-ext_foundation_ux_blitz",
  POST_OFFICE_PROVIDER = "rovo-ext_post_office_provider_no_harm_exp",
  USER_DEFAULT_SETTINGS_REVAMP = "rovo-ext_user_default_settings_revamp",
}

export enum PARAMETERS {
  isEnabled = "enabled",
  isDomContextImprovementEnabled = "rovo_extension_dom_context_improvement_enabled",
  isNtpSearchFiltersEnabled = "is_search_filters_enabled",
  is3pConnectorEnabled = "is3pConnectorEnabled",
  ntpAgentsDropdownTreatment = "treatment",
  isSettingsUiRevampEnabled = "is_settings_ui_revamp_enabled",
  isSettingsAdminSiteControlsEnabled = "is_settings_admin_site_controls_enabled",
  isEnabledForSiteAdmins = "isEnabledForSiteAdmins",
  isSessionReplayEnabled = "isSessionReplayEnabled",
  isDefaultToCustomLinksEnabled = "isEnabled",
  isAbleToViewCTA = "isAbleToViewCTA",
  isFoundationUxBlitzEnabled = "is_foundation_ux_blitz_enabled",
  isPostOfficeProviderEnabled = "isEnabled",
  isUserDefaultSettingsRevamp = "isUserDefaultSettingsRevamp",
}

export const activeExperiments = [
  {
    experimentName: EXPERIMENTS.DOM_CONTEXT_IMPROVEMENT,
    parameterName: PARAMETERS.isDomContextImprovementEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
    parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
    parameterName: PARAMETERS.is3pConnectorEnabled,
  },
  {
    experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
    parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
  },
  {
    experimentName: EXPERIMENTS.SETTINGS_ADMIN_SITE_CONTROLS,
    parameterName: PARAMETERS.isSettingsAdminSiteControlsEnabled,
  },
  {
    experimentName: EXPERIMENTS.SITE_ADMIN_SETTINGS,
    parameterName: PARAMETERS.isEnabledForSiteAdmins,
  },
  {
    experimentName: EXPERIMENTS.SESSION_REPLAY,
    parameterName: PARAMETERS.isSessionReplayEnabled,
  },
  {
    experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
    parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
  },
  {
    experimentName: EXPERIMENTS.EXTENSION_CTA_SITE_HOME,
    parameterName: PARAMETERS.isAbleToViewCTA,
  },
  {
    experimentName: EXPERIMENTS.FOUNDATION_UX_BLITZ,
    parameterName: PARAMETERS.isFoundationUxBlitzEnabled,
  },
  {
    experimentName: EXPERIMENTS.POST_OFFICE_PROVIDER,
    parameterName: PARAMETERS.isPostOfficeProviderEnabled,
  },
  {
    experimentName: EXPERIMENTS.USER_DEFAULT_SETTINGS_REVAMP,
    parameterName: PARAMETERS.isUserDefaultSettingsRevamp,
  },
];

export const isRovoExtensionDomContextImprovementEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.DOM_CONTEXT_IMPROVEMENT,
    parameterName: PARAMETERS.isDomContextImprovementEnabled,
    defaultValue: false,
  });
};

/** Expose user to DOM context improvement experiment if they send a message in the sidebar */
export const exposeUserToDomContextImprovementExperimentIfValid = (isSidebar: boolean): boolean => {
  if (isSidebar) {
    return isRovoExtensionDomContextImprovementEnabled();
  }

  return false;
};

export const isNtpSearchFiltersEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS,
    parameterName: PARAMETERS.isNtpSearchFiltersEnabled,
    defaultValue: false,
  });
};

export const isNtpSearchFilters3pConnectorsEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_SEARCH_FILTERS_3P_CONNECTORS,
    parameterName: PARAMETERS.is3pConnectorEnabled,
    defaultValue: false,
  });
};

export type NtpAgentsDropdownTreatment = "control" | "A";
/**
 * - "control": Feature disabled
 * - "A": Show dropdown on Chat tab
 */
export const getNtpAgentsDropdownTreatment = (): NtpAgentsDropdownTreatment => {
  return getExperiment({
    experimentName: EXPERIMENTS.NTP_AGENTS_DROPDOWN,
    parameterName: PARAMETERS.ntpAgentsDropdownTreatment,
    defaultValue: "control",
  }) as NtpAgentsDropdownTreatment;
};

export const isSettingsAdminSiteControlsExperimentEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.SETTINGS_ADMIN_SITE_CONTROLS,
    parameterName: PARAMETERS.isSettingsAdminSiteControlsEnabled,
    defaultValue: false,
  });
};

/**
 * Whether site admins may use company theming NTP preview (org admins are eligible separately).
 * Does **not** log experiment exposure — Home loads for all users; exposure is logged only when
 * a site admin actually enters preview (see `logCompanyThemingPreviewForSiteAdminsExperimentExposure`).
 */
export const isCompanyThemingPreviewEnabledForSiteAdmins = (): boolean => {
  return getExperimentNoExposure({
    experimentName: EXPERIMENTS.SITE_ADMIN_SETTINGS,
    parameterName: PARAMETERS.isEnabledForSiteAdmins,
    defaultValue: false,
  });
};

/** Call when a non–org-admin site admin is actively shown the theming preview (allocation + exposure). */
export const logCompanyThemingPreviewForSiteAdminsExperimentExposure = (): void => {
  manuallyLogExposure(EXPERIMENTS.SITE_ADMIN_SETTINGS);
};

// https://switcheroo.atlassian.com/ui/gates/6d883a6f-d1ae-451d-a056-51498e1d36b4/key/rovo_extension_default_to_custom_links
// Uses getExperiment (standard exposure) — fires exposure when Shortcuts component renders.
export const isDefaultToCustomLinksEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.DEFAULT_TO_CUSTOM_LINKS,
    parameterName: PARAMETERS.isDefaultToCustomLinksEnabled,
    defaultValue: false,
  });
};

/**
 * Session replay experiment flag (Statsig). New recording only starts when install is within the last hour
 * and this is enabled — see `SessionReplayInitializer` and `isInstalledWithinSessionReplayWindow`.
 * While the same Gas browser session id is active, replay is not torn down when these become false;
 * when Gas mints a new session id, `onAnalyticsWebClientSessionRotation` stops replay if still ineligible.
 */
export const isSessionReplayExperimentEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.SESSION_REPLAY,
    parameterName: PARAMETERS.isSessionReplayEnabled,
    defaultValue: false,
  });
};

/** Extension CTA on site home (For you): whether the user can see the install-extension CTA. */
export const isExtensionCtaSiteHomeEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.EXTENSION_CTA_SITE_HOME,
    parameterName: PARAMETERS.isAbleToViewCTA,
    defaultValue: false,
  });
};

// https://switcheroo.atlassian.com/ui/gates/c5b4cc92-cd1a-4c82-ba9b-8131bdcf49b2/key/rovo-ext_foundation_ux_blitz
export const isFoundationUxBlitzEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.FOUNDATION_UX_BLITZ,
    parameterName: PARAMETERS.isFoundationUxBlitzEnabled,
    defaultValue: false,
  });
};

// https://switcheroo.atlassian.com/ui/gates/4a2ff9f8-f377-40e0-9258-4886ba913881/key/rovo-ext_post_office_provider_no_harm_exp
export const isPostOfficeProviderEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.POST_OFFICE_PROVIDER,
    parameterName: PARAMETERS.isPostOfficeProviderEnabled,
    defaultValue: false,
  });
};

// https://switcheroo.atlassian.com/ui/gates/90807101-85da-4321-9be9-219eab788970/key/rovo-ext_user_default_settings_revamp
export const isUserDefaultSettingsRevampEnabled = (): boolean => {
  return getExperiment({
    experimentName: EXPERIMENTS.USER_DEFAULT_SETTINGS_REVAMP,
    parameterName: PARAMETERS.isUserDefaultSettingsRevamp,
    defaultValue: false,
  });
};
